'use client';

import { useSupportedCorporations } from '@commenda/api';
import {
  CountryInput,
  Input,
  Label,
  NumberInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
} from '@commenda/kakashi';
import { CorporationModel } from '@commenda/utils';
import type { CorporationType, Country } from '@prisma/client';
import {
  type OwnershipVariant,
  holdingTypeToVariant,
} from '../wrappers/Ownership/types';
import {
  AUTHORIZED_SHARES_DESCRIPTION_DEFAULT,
  partnerRoleLabelPlural,
} from '../wrappers/Ownership/copy';

const VARIANT_LABEL: Record<OwnershipVariant, string> = {
  shares: 'Shares (corporation)',
  membership: 'Membership (LLC)',
  partnership: 'Partnership',
};

// Per-variant fields the author configures: a seeded default value plus optional
// Visible (`canHide`) and Editable (`canLock`) toggles. Only fields whose value can
// be derived from the others are hideable (Authorized shares, ESOP); Issued shares
// and Price per share stay visible (lockable only). Hiding a field derives its
// value from the others — see computeShares.
const VARIANT_FIELDS: Record<
  OwnershipVariant,
  ReadonlyArray<{
    key: string;
    label: string;
    canHide?: boolean;
    canLock?: boolean;
    shareCapital?: boolean;
  }>
> = {
  shares: [
    {
      key: 'authorizedShares',
      label: 'Authorized shares',
      canHide: true,
      canLock: true,
    },
    { key: 'totalShareCapital', label: 'Share capital', shareCapital: true },
    {
      key: 'esopPercent',
      label: 'ESOP pool (%)',
      canHide: true,
      canLock: true,
    },
    { key: 'issuedShares', label: 'Issued shares', canLock: true },
    { key: 'pricePerShare', label: 'Price per share', canLock: true },
  ],
  membership: [
    {
      key: 'totalShareCapital',
      label: 'Total capital',
      canHide: true,
      canLock: true,
    },
    { key: 'totalUnits', label: 'Total units', canHide: true, canLock: true },
  ],
  partnership: [
    {
      key: 'totalShareCapital',
      label: 'Total capital',
      canHide: true,
      canLock: true,
    },
  ],
};

// Author config for the Ownership node. The holding-type variant is no longer
// picked directly — it's derived from the entity's country + jurisdiction +
// type (via its legal form's holding type, surfaced on the supported-
// corporations entry). The variant then drives which sections/toggles render.
export function OwnershipEditor({
  props,
  onUpdateProps,
}: {
  props: Record<string, unknown>;
  onUpdateProps: (key: string, value: unknown) => void;
}) {
  const { data: supportedCorporations } = useSupportedCorporations();

  const country = (props.holdingCountry as Country | undefined) ?? undefined;
  const jurisdiction =
    (props.holdingJurisdiction as string | undefined) ?? undefined;
  const corporationType =
    (props.holdingCorporationType as CorporationType | undefined) ?? undefined;
  const variant = (props.variant as OwnershipVariant | undefined) ?? undefined;

  const jurisdictionOptions =
    country && supportedCorporations
      ? CorporationModel.getJurisdictionList(country, supportedCorporations)
      : [];
  const entityTypeOptions =
    country && jurisdiction && supportedCorporations
      ? CorporationModel.getEntityTypes(
          country,
          jurisdiction,
          supportedCorporations
        )
      : [];

  const showJurisdictionField = jurisdictionOptions.length > 1;
  const showEntityTypeField = entityTypeOptions.length > 1;

  const soleJurisdiction = (c: Country): string | undefined => {
    if (!supportedCorporations) return undefined;
    const list = CorporationModel.getJurisdictionList(c, supportedCorporations);
    return list.length === 1 ? list[0].id : undefined;
  };
  const soleEntityType = (
    c: Country,
    j: string
  ): CorporationType | undefined => {
    if (!supportedCorporations) return undefined;
    const list = CorporationModel.getEntityTypes(c, j, supportedCorporations);
    return list.length === 1 ? list[0].code : undefined;
  };
  const deriveVariant = (
    c: Country | undefined,
    j: string | undefined,
    et: CorporationType | undefined
  ): OwnershipVariant | undefined => {
    if (!c || !j || !et) return undefined;
    return holdingTypeToVariant(
      supportedCorporations?.corporations?.[c]?.[j]?.[et]?.holdingType
    );
  };

  const handleCountry = (v: Country | undefined) => {
    const j = v ? soleJurisdiction(v) : undefined;
    const et = v && j ? soleEntityType(v, j) : undefined;
    onUpdateProps('holdingCountry', v);
    onUpdateProps('holdingJurisdiction', j);
    onUpdateProps('holdingCorporationType', et);
    onUpdateProps('variant', deriveVariant(v, j, et));
  };
  const handleJurisdiction = (v: string) => {
    const et = country ? soleEntityType(country, v) : undefined;
    onUpdateProps('holdingJurisdiction', v);
    onUpdateProps('holdingCorporationType', et);
    onUpdateProps('variant', deriveVariant(country, v, et));
  };
  const handleEntityType = (v: CorporationType) => {
    onUpdateProps('holdingCorporationType', v);
    onUpdateProps('variant', deriveVariant(country, jurisdiction, v));
  };

  // Per-field author config: default value + visibility/editability. Merges so
  // setting one doesn't drop the others on the same field.
  const fields =
    (props.fields as
      | Record<
          string,
          { default?: unknown; visible?: boolean; editable?: boolean }
        >
      | undefined) ?? {};
  const numericDefault = (key: string): number | undefined => {
    const v = fields[key]?.default;
    return typeof v === 'number' ? v : undefined;
  };
  const setFieldDefault = (key: string, value: number | undefined) =>
    onUpdateProps('fields', {
      ...fields,
      [key]: { ...fields[key], default: value },
    });
  const fieldVisible = (key: string) => fields[key]?.visible !== false;
  const fieldEditable = (key: string) => fields[key]?.editable !== false;
  const setFieldFlag = (
    key: string,
    flag: 'visible' | 'editable',
    value: boolean
  ) =>
    onUpdateProps('fields', {
      ...fields,
      [key]: { ...fields[key], [flag]: value },
    });

  // Jurisdiction share-capital minimum + helper copy (drives the sections' S5
  // validation). No content source yet, so the author enters it per form.
  const jurisdictionConfig =
    (props.jurisdiction as
      | { minShareCapital?: number; helperText?: string }
      | undefined) ?? {};
  const setJurisdiction = (patch: {
    minShareCapital?: number;
    helperText?: string;
  }) => onUpdateProps('jurisdiction', { ...jurisdictionConfig, ...patch });

  return (
    <div className="space-y-4">
      <h3 className="txt-small-medium text-text-secondary">Ownership</h3>
      <p className="txt-xs-regular text-text-tertiary">
        The holding type is derived from the entity&apos;s country, jurisdiction
        and type.
      </p>

      <div className="space-y-2">
        <Label>Country</Label>
        <CountryInput
          type="single"
          value={country ?? null}
          onValueChange={(v) => handleCountry(v ? (v as Country) : undefined)}
          placeholder="Select country"
        />
      </div>

      {showJurisdictionField ? (
        <div className="space-y-2">
          <Label>Jurisdiction</Label>
          <Select
            value={jurisdiction ?? undefined}
            onValueChange={(v) => handleJurisdiction(v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select jurisdiction" />
            </SelectTrigger>
            <SelectContent>
              {jurisdictionOptions.map((j) => (
                <SelectItem key={j.id} value={j.id}>
                  {j.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {showEntityTypeField ? (
        <div className="space-y-2">
          <Label>Entity type</Label>
          <Select
            key={jurisdiction ?? 'no-jurisdiction'}
            value={corporationType ?? undefined}
            onValueChange={(v) => handleEntityType(v as CorporationType)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select entity type" />
            </SelectTrigger>
            <SelectContent>
              {entityTypeOptions.map((et) => (
                <SelectItem key={et.code} value={et.code}>
                  {et.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {variant ? (
        <p className="txt-xs-regular text-text-tertiary">
          Holding type:{' '}
          <span className="text-text-secondary">{VARIANT_LABEL[variant]}</span>
        </p>
      ) : country && !showJurisdictionField && !showEntityTypeField ? (
        <p className="txt-xs-regular text-text-warning">
          This entity isn’t supported for ownership yet — no holding type is
          configured for it, so the form will show a “not supported” message.
        </p>
      ) : country ? (
        <p className="txt-xs-regular text-text-tertiary">
          Select the {showJurisdictionField ? 'jurisdiction and ' : ''}entity
          type to derive the holding type.
        </p>
      ) : (
        <p className="txt-xs-regular text-text-tertiary">
          Pick a country to derive the holding type.
        </p>
      )}

      {variant === 'partnership' ? (
        <div className="space-y-2">
          <Label>Partner structure</Label>
          <Select
            value={
              (props.partnershipStructure as string | undefined) ?? 'gp_and_lp'
            }
            onValueChange={(v) => onUpdateProps('partnershipStructure', v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select partner structure" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="gp_and_lp">
                General &amp; {partnerRoleLabelPlural(country)}
              </SelectItem>
              <SelectItem value="lp_only">
                {partnerRoleLabelPlural(country)} only (no GP)
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="txt-xs-regular text-text-tertiary">
            Whether the partnership designates a General Partner.{' '}
            {`“${partnerRoleLabelPlural(country)} only”`} (e.g. Indian LLP)
            hides the GP field — all partners are{' '}
            {partnerRoleLabelPlural(country)}.
          </p>
        </div>
      ) : null}

      {variant === 'shares' ? (
        <div className="space-y-2">
          <Label>Authorized shares description</Label>
          <Textarea
            placeholder={AUTHORIZED_SHARES_DESCRIPTION_DEFAULT}
            value={
              (props.copy as { authorizedSharesDescription?: string } | undefined)
                ?.authorizedSharesDescription ?? ''
            }
            onChange={(e) =>
              onUpdateProps('copy', {
                ...((props.copy as Record<string, unknown>) ?? {}),
                authorizedSharesDescription: e.target.value,
              })
            }
          />
          <p className="txt-xs-regular text-text-tertiary">
            Shown under the Authorized Shares heading. Leave blank to use the
            default copy (shown as the placeholder).
          </p>
        </div>
      ) : null}

      {variant ? (
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Fields</Label>
            <p className="txt-xs-regular text-text-tertiary">
              Set a default value, hide a field (its value is derived from the
              others where it makes sense), or lock it at its default.
            </p>
          </div>
          {VARIANT_FIELDS[variant].map((f) => {
            // Share Capital uses its own enable toggle (which also derives price);
            // its default + input only apply while enabled.
            const shareCapitalEnabled =
              (props.shareCapitalEnabled as boolean) ?? false;
            const visible = f.shareCapital
              ? shareCapitalEnabled
              : fieldVisible(f.key);
            return (
              <div
                key={f.key}
                className="space-y-3 rounded-md border border-border-secondary p-3"
              >
                <Label className="txt-xs-regular text-text-secondary">
                  {f.label}
                </Label>
                {f.shareCapital ? (
                  <div className="flex items-center justify-between gap-3">
                    <span className="txt-xs-regular text-text-tertiary">
                      Enable (derives price)
                    </span>
                    <Switch
                      checked={shareCapitalEnabled}
                      onCheckedChange={(c) =>
                        onUpdateProps('shareCapitalEnabled', c)
                      }
                    />
                  </div>
                ) : null}
                {visible ? (
                  <NumberInput
                    formatWithCommas
                    placeholder="No default"
                    value={numericDefault(f.key)}
                    onChange={(n) => setFieldDefault(f.key, n ?? undefined)}
                  />
                ) : null}
                {f.canHide || f.canLock ? (
                  <div className="space-y-2">
                    {f.canHide ? (
                      <div className="flex items-center justify-between gap-3">
                        <span className="txt-xs-regular text-text-tertiary">
                          Visible
                        </span>
                        <Switch
                          checked={visible}
                          onCheckedChange={(c) =>
                            setFieldFlag(f.key, 'visible', c)
                          }
                        />
                      </div>
                    ) : null}
                    {f.canLock ? (
                      <div className="flex items-center justify-between gap-3">
                        <span className="txt-xs-regular text-text-tertiary">
                          Editable
                        </span>
                        <Switch
                          checked={fieldEditable(f.key)}
                          disabled={!visible}
                          onCheckedChange={(c) =>
                            setFieldFlag(f.key, 'editable', c)
                          }
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {variant ? (
        <div className="space-y-2">
          <Label>Minimum share capital (optional)</Label>
          <NumberInput
            formatWithCommas
            placeholder="e.g. 25000"
            value={jurisdictionConfig.minShareCapital}
            onChange={(n) =>
              setJurisdiction({ minShareCapital: n ?? undefined })
            }
          />
          <Input
            placeholder="Helper text (e.g. GmbH requires €25,000)"
            value={jurisdictionConfig.helperText ?? ''}
            onChange={(e) =>
              setJurisdiction({ helperText: e.target.value || undefined })
            }
          />
        </div>
      ) : null}
    </div>
  );
}
